import React from "react";
import { Box, Typography } from "@mui/material";
import {
  AcUnitOutlined,
  ChairOutlined,
  ElectricalServicesOutlined,
  HandymanOutlined,
  HealthAndSafetyOutlined,
  HouseSidingOutlined,
  KitchenOutlined,
  PestControlOutlined,
  PlumbingOutlined,
  RoofingOutlined,
  VerifiedOutlined,
  YardOutlined,
} from "@mui/icons-material";
import type { MaintenanceCategory } from "../../../../Services/maintenanceApi";
import { DASH } from "../../dashboard/tokens";
import type { DueInfo } from "./maintenanceSchedule";

const ICONS: Record<MaintenanceCategory, React.ElementType> = {
  hvac: AcUnitOutlined,
  plumbing: PlumbingOutlined,
  electrical: ElectricalServicesOutlined,
  roofing: RoofingOutlined,
  exterior: HouseSidingOutlined,
  interior: ChairOutlined,
  appliances: KitchenOutlined,
  safety: HealthAndSafetyOutlined,
  landscaping: YardOutlined,
  pest: PestControlOutlined,
  other: HandymanOutlined,
};

export const CategoryIcon: React.FC<{ category?: MaintenanceCategory | string; size?: number }> = ({
  category,
  size = 36,
}) => {
  const Icon = ICONS[(category as MaintenanceCategory) || "other"] || HandymanOutlined;
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "10px",
        bgcolor: DASH.peach,
        color: DASH.teal,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: size * 0.55 }} />
    </Box>
  );
};

const DUE_STYLES: Record<DueInfo["state"], { bg: string; fg: string }> = {
  overdue: { bg: DASH.redTint, fg: DASH.red },
  "due-soon": { bg: DASH.amberTint, fg: DASH.amber },
  upcoming: { bg: DASH.tealTint, fg: DASH.teal },
};

/** "Expires in 3 months" — a warranty's countdown, in DueChip's style. */
export const expiryInfo = (info: DueInfo | null): DueInfo | null =>
  info && { ...info, label: info.label.replace(/^Due/, "Expires") };

export const WarrantyIcon: React.FC<{ size?: number }> = ({ size = 36 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: "10px",
      bgcolor: DASH.amberTint,
      color: DASH.amber,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <VerifiedOutlined sx={{ fontSize: size * 0.55 }} />
  </Box>
);

export const DueChip: React.FC<{ info: DueInfo | null }> = ({ info }) => {
  if (!info) return null;
  const s = DUE_STYLES[info.state];
  return (
    <Typography
      component="span"
      sx={{
        display: "inline-block",
        px: 2,
        py: 0.5,
        borderRadius: 50,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: "nowrap",
        bgcolor: s.bg,
        color: s.fg,
      }}
      data-testid={`due-chip-${info.state}`}
    >
      {info.label}
    </Typography>
  );
};
